import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '../App';
import { Navigation } from '../components/Navigation';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Trophy, Medal, DollarSign, TrendingUp, Crown, Target, Flag, Award, User, BarChart3, X, Check, Trash2, Users } from 'lucide-react';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceDot } from 'recharts';

const TABS = [
  { key: 'lowest_score', label: 'Lowest Score', icon: Target },
  { key: 'best_vs_par', label: 'Best vs Par', icon: TrendingUp },
  { key: 'most_birdies', label: 'Most Birdies', icon: Flag },
  { key: 'teams', label: 'Team Wins', icon: Users },
  { key: 'my_rounds', label: 'My Rounds', icon: User },
  { key: 'wins', label: 'Bundle Wins', icon: DollarSign },
];

const formatVsParLabel = (v) => v === 0 ? 'E' : v > 0 ? `+${v}` : `${v}`;

const DeltaBadge = ({ val, label, invert }) => {
  if (val == null || val === 0) return null;
  const isGood = invert ? val > 0 : val < 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-semibold ${isGood ? 'text-green-600' : 'text-red-500'}`}>
      {val > 0 ? '+' : ''}{val} {label}
    </span>
  );
};

function RoundTooltip({ active, payload, bestIdx }) {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  const hasDelta = d.delta_score != null;
  const isBest = payload[0]?.payload && payload[0]?.index === bestIdx;
  return (
    <div className="bg-white rounded-xl shadow-lg border border-bundle-cream-dark p-3 min-w-[210px] max-w-[260px]" data-testid="round-tooltip">
      {isBest && (
        <div className="flex items-center gap-1.5 mb-1.5 px-2 py-1 bg-bundle-gold/15 rounded-md">
          <Crown className="w-3.5 h-3.5 text-bundle-gold" />
          <span className="font-heading text-xs font-bold text-bundle-gold-dark">Personal Record</span>
        </div>
      )}
      <p className="font-heading font-bold text-sm text-bundle-green mb-1 truncate">{d.course}</p>
      <p className="font-body text-xs text-gray-500 mb-2">{d.date} &middot; {d.holes} holes</p>
      <div className="flex items-baseline gap-2 mb-1">
        <span className="font-heading text-2xl font-bold text-bundle-green">{d.score}</span>
        <span className={`font-body text-sm font-semibold ${d.vs_par < 0 ? 'text-red-600' : d.vs_par === 0 ? 'text-bundle-green' : 'text-gray-600'}`}>
          ({formatVsParLabel(d.vs_par)})
        </span>
        {d.handicap != null && <span className="font-body text-xs text-red-500 ml-auto">HCP {d.handicap}</span>}
      </div>
      {hasDelta && (
        <div className="flex items-center gap-2 mb-2 px-1.5 py-1 bg-gray-50 rounded-md">
          <DeltaBadge val={d.delta_score} label="strokes" />
          <DeltaBadge val={d.delta_birdies} label="birdies" invert />
          <DeltaBadge val={d.delta_putts} label="putts" />
        </div>
      )}
      <div className="grid grid-cols-5 gap-1 text-center mb-2">
        {d.eagles > 0 && <div><p className="font-body text-xs font-bold text-purple-600">{d.eagles}</p><p className="font-body text-[10px] text-gray-400">Eagle</p></div>}
        <div><p className="font-body text-xs font-bold text-red-600">{d.birdies}</p><p className="font-body text-[10px] text-gray-400">Birdie</p></div>
        <div><p className="font-body text-xs font-bold text-bundle-green">{d.pars}</p><p className="font-body text-[10px] text-gray-400">Par</p></div>
        <div><p className="font-body text-xs font-bold text-amber-600">{d.bogeys}</p><p className="font-body text-[10px] text-gray-400">Bogey</p></div>
        {(d.double_bogeys || 0) > 0 && <div><p className="font-body text-xs font-bold text-gray-600">{d.double_bogeys}</p><p className="font-body text-[10px] text-gray-400">Dbl+</p></div>}
      </div>
      <div className="flex justify-between text-xs font-body text-gray-500 border-t border-bundle-cream-dark pt-1.5">
        <span>{d.putts || 0} putts</span>
        <span>Avg: {d.moving_avg}</span>
      </div>
      {d.round_id && <p className="font-body text-[10px] text-bundle-gold mt-1.5 text-center">Click dot to view scorecard</p>}
    </div>
  );
}

export default function Leaderboard() {
  const { user, loading, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [leaderboard, setLeaderboard] = useState([]);
  const [myRank, setMyRank] = useState(null);
  const [period, setPeriod] = useState('all');
  const [activeTab, setActiveTab] = useState('lowest_score');
  const [loadingData, setLoadingData] = useState(true);
  // Compare state
  const [compareMode, setCompareMode] = useState(false);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [compareData, setCompareData] = useState(null);
  const [loadingCompare, setLoadingCompare] = useState(false);
  // Trend state
  const [trendData, setTrendData] = useState(null);
  const [showTrend, setShowTrend] = useState(true);
  const [trendCourseFilter, setTrendCourseFilter] = useState(null); // null = all courses

  useEffect(() => {
    if (!loading && !user) navigate('/');
  }, [loading, user, navigate]);

  useEffect(() => {
    if (user) {
      fetchLeaderboard();
      if (activeTab === 'wins') fetchMyRank();
      if (activeTab === 'my_rounds' && !trendData) fetchTrend();
    }
  }, [user, period, activeTab]);

  const fetchLeaderboard = async () => {
    setLoadingData(true);
    try {
      let url;
      if (activeTab === 'wins') {
        url = `${API}/leaderboard?period=${period}&limit=10`;
      } else if (activeTab === 'my_rounds') {
        url = `${API}/leaderboard/my-rounds`;
      } else if (activeTab === 'teams') {
        url = `${API}/leaderboard/teams?period=${period}&limit=25`;
      } else {
        url = `${API}/leaderboard/rounds?category=${activeTab}&period=${period}&limit=10`;
      }
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) setLeaderboard(await response.json());
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    } finally {
      setLoadingData(false);
    }
  };

  const fetchMyRank = async () => {
    try {
      const response = await fetch(`${API}/leaderboard/my-rank`, { credentials: 'include' });
      if (response.ok) setMyRank(await response.json());
    } catch (error) {
      console.error('Error fetching rank:', error);
    }
  };

  const toggleCourseSelection = (courseName) => {
    setSelectedCourses(prev =>
      prev.includes(courseName) ? prev.filter(c => c !== courseName) : prev.length < 4 ? [...prev, courseName] : prev
    );
  };

  const runComparison = async () => {
    if (selectedCourses.length < 2) return;
    setLoadingCompare(true);
    try {
      const res = await fetch(`${API}/leaderboard/compare-courses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ courses: selectedCourses }),
      });
      if (res.ok) setCompareData(await res.json());
    } catch (error) {
      console.error('Compare error:', error);
    } finally {
      setLoadingCompare(false);
    }
  };

  const exitCompare = () => {
    setCompareMode(false);
    setSelectedCourses([]);
    setCompareData(null);
  };

  const fetchTrend = async () => {
    try {
      const res = await fetch(`${API}/leaderboard/score-trend`, { credentials: 'include' });
      if (res.ok) setTrendData(await res.json());
    } catch (e) {
      console.error('Trend fetch error:', e);
    }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bundle-cream">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-bundle-green"></div>
      </div>
    );
  }

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="w-6 h-6 text-bundle-gold" />;
    if (rank === 2) return <Medal className="w-6 h-6 text-gray-400" />;
    if (rank === 3) return <Medal className="w-6 h-6 text-amber-600" />;
    return <span className="font-heading text-lg font-bold text-gray-500">{rank}</span>;
  };

  const formatVsPar = (val) => {
    if (val === 0) return 'E';
    return val > 0 ? `+${val}` : `${val}`;
  };

  return (
    <div className="min-h-screen bg-bundle-cream pb-mobile-nav md:pb-8">
      <Navigation user={user} onLogout={logout} />

      <div className="max-w-4xl mx-auto px-4 md:px-12 pt-20 md:pt-24">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div>
            <h1 className="font-heading font-bold text-3xl md:text-4xl text-bundle-green mb-2" data-testid="leaderboard-title">
              2026 Leaderboard (Earnings)
            </h1>
            <p className="font-body text-gray-600">See who's on top</p>
          </div>
          {activeTab !== 'my_rounds' && (
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger data-testid="period-filter" className="w-40 bg-white border-bundle-cream-dark">
                <SelectValue placeholder="Time Period" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Time</SelectItem>
                <SelectItem value="month">This Month</SelectItem>
                <SelectItem value="week">This Week</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 overflow-x-auto scrollbar-none" data-testid="leaderboard-tabs">
          {TABS.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                data-testid={`tab-${tab.key}`}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-1.5 px-4 py-2.5 rounded-lg font-body text-sm font-medium whitespace-nowrap transition-all ${
                  isActive
                    ? 'bg-bundle-green text-white shadow-md'
                    : 'bg-white text-gray-600 hover:bg-bundle-cream-dark'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* My Rank Card (only for Bundle Wins) */}
        {activeTab === 'wins' && myRank?.rank && (
          <Card className="mb-6 bg-gradient-bundle border-0 overflow-hidden" data-testid="my-rank-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                    <TrendingUp className="w-7 h-7 text-bundle-gold" />
                  </div>
                  <div>
                    <p className="font-body text-sm text-bundle-cream/80">Your Rank</p>
                    <p className="font-heading text-3xl font-bold text-white">#{myRank.rank}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-body text-sm text-bundle-cream/80">Total Winnings</p>
                  <p className="font-heading text-2xl font-bold text-bundle-gold">
                    ${myRank.total_winnings?.toFixed(0) || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Personal Record Card (My Rounds tab) */}
        {activeTab === 'my_rounds' && leaderboard.length > 0 && (() => {
          const best = leaderboard[0]; // already sorted by best score
          const totalRounds = leaderboard.reduce((s, e) => s + e.rounds_played, 0);
          const overallAvg = leaderboard.length > 0 ? Math.round(leaderboard.reduce((s, e) => s + e.avg_score * e.rounds_played, 0) / totalRounds * 10) / 10 : 0;
          return (
            <Card className="mb-4 bg-gradient-to-r from-bundle-green to-bundle-green-light border-0 overflow-hidden" data-testid="personal-record-card">
              <CardContent className="p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-bundle-gold/20 rounded-lg flex items-center justify-center">
                    <Crown className="w-5 h-5 text-bundle-gold" />
                  </div>
                  <div>
                    <p className="font-body text-xs text-bundle-cream/70 uppercase tracking-wider">Personal Record</p>
                    <p className="font-heading text-lg font-bold text-white">{best.course_name}</p>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-3">
                  <div className="bg-white/10 rounded-lg p-3 text-center">
                    <p className="font-heading text-2xl font-bold text-bundle-gold">{best.best_score}</p>
                    <p className="font-body text-[10px] text-bundle-cream/70 uppercase">Best Score</p>
                  </div>
                  <div className="bg-white/10 rounded-lg p-3 text-center">
                    <p className={`font-heading text-2xl font-bold ${best.best_vs_par <= 0 ? 'text-bundle-gold' : 'text-white'}`}>
                      {formatVsPar(best.best_vs_par)}
                    </p>
                    <p className="font-body text-[10px] text-bundle-cream/70 uppercase">vs Par</p>
                  </div>
                  <div className="bg-white/10 rounded-lg p-3 text-center">
                    <p className="font-heading text-2xl font-bold text-white">{totalRounds}</p>
                    <p className="font-body text-[10px] text-bundle-cream/70 uppercase">Rounds</p>
                  </div>
                  <div className="bg-white/10 rounded-lg p-3 text-center">
                    <p className="font-heading text-2xl font-bold text-white">{overallAvg}</p>
                    <p className="font-body text-[10px] text-bundle-cream/70 uppercase">Avg Score</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })()}

        {/* Compare button for My Rounds */}
        {activeTab === 'my_rounds' && !compareData && leaderboard.length >= 2 && (
          <div className="flex items-center gap-3 mb-4">
            <Button
              data-testid="compare-courses-btn"
              variant={compareMode ? "default" : "outline"}
              size="sm"
              onClick={() => { setCompareMode(!compareMode); setSelectedCourses([]); }}
              className={compareMode ? "btn-bundle" : "border-bundle-green text-bundle-green hover:bg-bundle-green/5"}
            >
              <BarChart3 className="w-4 h-4 mr-1.5" />
              {compareMode ? 'Selecting...' : 'Compare Courses'}
            </Button>
            {compareMode && (
              <>
                <span className="font-body text-sm text-gray-500">
                  {selectedCourses.length}/4 selected
                </span>
                <Button
                  data-testid="run-compare-btn"
                  size="sm"
                  disabled={selectedCourses.length < 2 || loadingCompare}
                  onClick={runComparison}
                  className="btn-bundle"
                >
                  {loadingCompare ? <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-1" /> : <Check className="w-4 h-4 mr-1" />}
                  Compare
                </Button>
                <Button variant="ghost" size="sm" onClick={() => { setCompareMode(false); setSelectedCourses([]); }}>
                  <X className="w-4 h-4" /> Cancel
                </Button>
              </>
            )}
          </div>
        )}

        {/* Scoring Trend Chart for My Rounds */}
        {activeTab === 'my_rounds' && !compareData && !compareMode && trendData?.timeline?.length >= 2 && (
          <Card className="bg-white border-bundle-cream-dark overflow-hidden mb-4" data-testid="score-trend-chart">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading font-bold text-base text-bundle-green flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" /> Scoring Trend
                  {trendCourseFilter && (
                    <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 bg-bundle-green/10 rounded-full text-xs font-body font-medium text-bundle-green">
                      {trendCourseFilter}
                      <button data-testid="clear-course-filter" onClick={() => setTrendCourseFilter(null)} className="hover:text-red-500 ml-0.5"><X className="w-3 h-3" /></button>
                    </span>
                  )}
                </h3>
                <button
                  data-testid="toggle-trend-btn"
                  onClick={() => setShowTrend(!showTrend)}
                  className="font-body text-xs text-gray-500 hover:text-bundle-green transition-colors"
                >
                  {showTrend ? 'Hide' : 'Show'}
                </button>
              </div>
              {showTrend && (() => {
                const filtered = trendCourseFilter
                  ? trendData.timeline.filter(t => t.course === trendCourseFilter)
                  : trendData.timeline;
                // Recompute moving avg for filtered data
                const chartData = filtered.map((t, i) => {
                  const window = filtered.slice(Math.max(0, i - 4), i + 1);
                  const prev = i > 0 ? filtered[i - 1] : null;
                  return {
                    ...t,
                    moving_avg: Math.round(window.reduce((s, w) => s + w.score, 0) / window.length * 10) / 10,
                    delta_score: prev ? t.score - prev.score : null,
                    delta_putts: prev ? t.putts - prev.putts : null,
                    delta_birdies: prev ? t.birdies - prev.birdies : null,
                    prev_course: prev?.course || null,
                  };
                });
                const hasHandicap = chartData.some(d => d.handicap != null);
                // Find best round (lowest score)
                const bestIdx = chartData.reduce((bi, d, i) => d.score < chartData[bi].score ? i : bi, 0);
                const bestRound = chartData[bestIdx];
                // Custom dot renderer that highlights best round
                const renderDot = (props) => {
                  const { cx, cy, index } = props;
                  if (index === bestIdx) {
                    return (
                      <g key={`best-${index}`}>
                        <circle cx={cx} cy={cy} r={8} fill="#c5993e" opacity={0.25} />
                        <circle cx={cx} cy={cy} r={5} fill="#c5993e" stroke="#fff" strokeWidth={2} />
                        <text x={cx} y={cy - 12} textAnchor="middle" fill="#c5993e" fontSize={10} fontWeight="bold">PR</text>
                      </g>
                    );
                  }
                  return <circle key={`dot-${index}`} cx={cx} cy={cy} r={4} fill="#1a472a" stroke="#1a472a" strokeWidth={2} />;
                };
                return chartData.length >= 2 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={chartData} margin={{ top: 15, right: hasHandicap ? 50 : 20, bottom: 5, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7d4" />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 11, fill: '#6b7280' }}
                        tickFormatter={d => { const [,m,day] = (d||'').split('-'); return m && day ? `${m}/${day}` : d; }}
                      />
                      <YAxis yAxisId="left" tick={{ fontSize: 11, fill: '#6b7280' }} domain={['dataMin - 2', 'dataMax + 2']} />
                      {hasHandicap && (
                        <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: '#dc2626' }} domain={['dataMin - 2', 'dataMax + 2']} label={{ value: 'HCP', angle: 90, position: 'insideRight', style: { fontSize: 10, fill: '#dc2626' } }} />
                      )}
                      <Tooltip content={<RoundTooltip bestIdx={bestIdx} />} />
                      <Legend
                        formatter={(value) => value === 'score' ? 'Round Score' : value === 'moving_avg' ? '5-Round Avg' : 'Handicap Index'}
                        wrapperStyle={{ fontSize: 12 }}
                      />
                      <Line yAxisId="left" type="monotone" dataKey="score" stroke="#1a472a" strokeWidth={2} dot={renderDot} activeDot={{ r: 7, cursor: 'pointer', onClick: (e, payload) => { if (payload?.payload?.round_id) navigate(`/live-round/${payload.payload.round_id}`); } }} />
                      <Line yAxisId="left" type="monotone" dataKey="moving_avg" stroke="#c5993e" strokeWidth={2} strokeDasharray="5 5" dot={false} />
                      {hasHandicap && (
                        <Line yAxisId="right" type="monotone" dataKey="handicap" stroke="#dc2626" strokeWidth={2} strokeDasharray="3 3" dot={{ r: 3, fill: '#dc2626' }} />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="font-body text-sm text-gray-500 text-center py-8">Need at least 2 rounds at this course to show a trend.</p>
                );
              })()}
            </CardContent>
          </Card>
        )}

        {/* Comparison Results */}
        {compareData ? (
          <ComparisonView data={compareData} formatVsPar={formatVsPar} onClose={exitCompare} />
        ) : (
          /* Leaderboard Table */
          <Card className="bg-white border-bundle-cream-dark overflow-hidden" data-testid="leaderboard-table">
            <CardContent className="p-0">
              {activeTab === 'wins' ? (
                <WinsTable loadingData={loadingData} leaderboard={leaderboard} user={user} getRankIcon={getRankIcon} navigate={navigate} isAdmin={user?.is_admin} onAfterDelete={fetchLeaderboard} />
              ) : activeTab === 'teams' ? (
                <TeamsTable loadingData={loadingData} leaderboard={leaderboard} getRankIcon={getRankIcon} user={user} />
              ) : activeTab === 'my_rounds' ? (
                <MyRoundsTable 
                  loadingData={loadingData} 
                  leaderboard={leaderboard} 
                  getRankIcon={getRankIcon} 
                  formatVsPar={formatVsPar} 
                  navigate={navigate} 
                  compareMode={compareMode} 
                  selectedCourses={selectedCourses} 
                  toggleCourseSelection={toggleCourseSelection} 
                  trendCourseFilter={trendCourseFilter} 
                  setTrendCourseFilter={setTrendCourseFilter}
                  onDeleteRound={() => { fetchLeaderboard(); fetchTrend(); }}
                  isAdmin={user?.is_admin}
                />
              ) : (
                <RoundsTable loadingData={loadingData} leaderboard={leaderboard} activeTab={activeTab} getRankIcon={getRankIcon} formatVsPar={formatVsPar} isAdmin={user?.is_admin} onAfterDelete={fetchLeaderboard} />
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function WinsTable({ loadingData, leaderboard, user, getRankIcon, navigate, isAdmin, onAfterDelete }) {
  const handleAdminDelete = async (e, entry) => {
    e.stopPropagation();
    if (!window.confirm(`Admin: clear ALL bundle wins for ${entry.name}? This removes their leaderboard entries and resets their bundle stats.`)) return;
    try {
      const res = await fetch(`${API}/admin/bundle-wins/by-user/${encodeURIComponent(entry.name)}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (res.ok) {
        const d = await res.json();
        toast.success(`Cleared ${entry.name}'s wins (${d.deleted_count || 0} records)`);
        if (onAfterDelete) onAfterDelete();
      } else {
        toast.error('Failed to delete');
      }
    } catch { toast.error('Network error'); }
  };

  return (
    <>
      <div className="grid grid-cols-12 gap-4 px-6 py-4 bg-bundle-cream/50 border-b border-bundle-cream-dark">
        <div className="col-span-1 font-body text-xs uppercase tracking-wide text-gray-500 font-semibold">Rank</div>
        <div className={`${isAdmin ? 'col-span-4' : 'col-span-5'} font-body text-xs uppercase tracking-wide text-gray-500 font-semibold`}>Player</div>
        <div className="col-span-2 font-body text-xs uppercase tracking-wide text-gray-500 font-semibold text-center">Wins</div>
        <div className="col-span-2 font-body text-xs uppercase tracking-wide text-gray-500 font-semibold text-center">HC</div>
        <div className="col-span-2 font-body text-xs uppercase tracking-wide text-gray-500 font-semibold text-right">Winnings</div>
        {isAdmin && <div className="col-span-1 font-body text-xs uppercase tracking-wide text-gray-500 font-semibold text-right">Director</div>}
      </div>
      {loadingData ? (
        <div className="p-12 text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-bundle-green mx-auto"></div>
        </div>
      ) : leaderboard.length === 0 ? (
        <EmptyState message="Post your first bundle win to appear on the leaderboard!" actionLabel="Post Bundle Win" onAction={() => navigate('/post-story')} />
      ) : (
        <div className="divide-y divide-bundle-cream-dark">
          {leaderboard.map((entry, index) => {
            const isCurrentUser = entry.name === user.name;
            return (
              <div
                key={index}
                data-testid={`wins-row-${index}`}
                className={`grid grid-cols-12 gap-4 px-6 py-4 items-center transition-colors ${
                  isCurrentUser ? 'rank-highlight bg-bundle-gold/5' : 'hover:bg-bundle-cream/30'
                } ${entry.rank <= 3 ? 'bg-bundle-cream/20' : ''}`}
              >
                <div className="col-span-1 flex items-center justify-center">{getRankIcon(entry.rank)}</div>
                <div className={isAdmin ? 'col-span-4' : 'col-span-5'}>
                  <p className={`font-body font-semibold ${isCurrentUser ? 'text-bundle-gold-dark' : 'text-bundle-green'}`}>
                    {entry.name}
                    {isCurrentUser && <span className="ml-2 text-xs text-bundle-gold">(You)</span>}
                  </p>
                </div>
                <div className="col-span-2 text-center">
                  <span className="inline-flex items-center justify-center w-8 h-8 bg-bundle-green/10 rounded-full">
                    <span className="font-body font-semibold text-bundle-green">{entry.wins_count}</span>
                  </span>
                </div>
                <div className="col-span-2 text-center">
                  <span className="font-body text-gray-600">{entry.handicap?.toFixed(1) || '--'}</span>
                </div>
                <div className="col-span-2 text-right">
                  <span className="font-heading text-lg font-bold text-bundle-green">${entry.total_winnings?.toFixed(0) || 0}</span>
                </div>
                {isAdmin && (
                  <div className="col-span-1 text-right">
                    <button
                      data-testid={`admin-delete-wins-${index}`}
                      onClick={(e) => handleAdminDelete(e, entry)}
                      className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
                      title={`Admin: clear ${entry.name}'s wins`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

function RoundsTable({ loadingData, leaderboard, activeTab, getRankIcon, formatVsPar, isAdmin, onAfterDelete }) {
  const isBirdies = activeTab === 'most_birdies';

  const handleAdminDelete = async (e, entry) => {
    e.stopPropagation();
    if (!entry.source || !entry.round_id) {
      toast.error('This round cannot be deleted (missing id)');
      return;
    }
    if (!window.confirm(`Admin: delete ${entry.user_name}'s round at ${entry.course_name} (${entry.total_score})?`)) return;
    try {
      const res = await fetch(`${API}/admin/round/${entry.source}/${encodeURIComponent(entry.round_id)}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (res.ok) {
        toast.success('Round deleted');
        if (onAfterDelete) onAfterDelete();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to delete');
      }
    } catch { toast.error('Network error'); }
  };

  return (
    <>
      <div className="grid grid-cols-12 gap-2 px-6 py-4 bg-bundle-cream/50 border-b border-bundle-cream-dark">
        <div className="col-span-1 font-body text-xs uppercase tracking-wide text-gray-500 font-semibold">#</div>
        <div className="col-span-3 font-body text-xs uppercase tracking-wide text-gray-500 font-semibold">Player</div>
        <div className={`${isAdmin ? 'col-span-2' : 'col-span-3'} font-body text-xs uppercase tracking-wide text-gray-500 font-semibold`}>Course</div>
        <div className="col-span-2 font-body text-xs uppercase tracking-wide text-gray-500 font-semibold text-center">
          {isBirdies ? 'Birdies' : 'Score'}
        </div>
        <div className="col-span-1 font-body text-xs uppercase tracking-wide text-gray-500 font-semibold text-center">vs Par</div>
        <div className="col-span-2 font-body text-xs uppercase tracking-wide text-gray-500 font-semibold text-right">Holes</div>
        {isAdmin && <div className="col-span-1 font-body text-xs uppercase tracking-wide text-gray-500 font-semibold text-right">Director</div>}
      </div>
      {loadingData ? (
        <div className="p-12 text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-bundle-green mx-auto"></div>
        </div>
      ) : leaderboard.length === 0 ? (
        <EmptyState message="Complete a public live round to appear here!" />
      ) : (
        <div className="divide-y divide-bundle-cream-dark">
          {leaderboard.map((entry, index) => (
            <div
              key={index}
              data-testid={`round-row-${index}`}
              className={`grid grid-cols-12 gap-2 px-6 py-4 items-center transition-colors hover:bg-bundle-cream/30 ${
                entry.rank <= 3 ? 'bg-bundle-cream/20' : ''
              }`}
            >
              <div className="col-span-1 flex items-center justify-center">{getRankIcon(entry.rank)}</div>
              <div className="col-span-3">
                <p className="font-body font-semibold text-bundle-green truncate">{entry.user_name}</p>
              </div>
              <div className={isAdmin ? 'col-span-2' : 'col-span-3'}>
                <p className="font-body text-sm text-gray-600 truncate">{entry.course_name}</p>
              </div>
              <div className="col-span-2 text-center">
                {isBirdies ? (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-red-50 text-red-700 rounded-full font-body font-bold text-sm">
                    <Flag className="w-3.5 h-3.5" /> {entry.birdies || 0}
                  </span>
                ) : (
                  <span className="font-heading text-lg font-bold text-bundle-green">{entry.total_score}</span>
                )}
              </div>
              <div className="col-span-1 text-center">
                <span className={`font-body font-semibold text-sm ${
                  entry.score_vs_par < 0 ? 'text-red-600' : entry.score_vs_par === 0 ? 'text-bundle-green' : 'text-gray-700'
                }`}>
                  {formatVsPar(entry.score_vs_par)}
                </span>
              </div>
              <div className="col-span-2 text-right">
                <span className="font-body text-sm text-gray-500">{entry.holes_completed}</span>
              </div>
              {isAdmin && (
                <div className="col-span-1 text-right">
                  <button
                    data-testid={`admin-delete-round-${index}`}
                    onClick={(e) => handleAdminDelete(e, entry)}
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
                    title="Admin: delete this round"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function MyRoundsTable({ loadingData, leaderboard, getRankIcon, formatVsPar, navigate, compareMode, selectedCourses, toggleCourseSelection, trendCourseFilter, setTrendCourseFilter, onDeleteRound, isAdmin }) {
  
  const handleDelete = async (e, entry) => {
    e.stopPropagation();
    const confirmMsg = `Delete all ${entry.rounds_played} round(s) at ${entry.course_name}?`;
    if (!window.confirm(confirmMsg)) return;
    
    try {
      // Delete from live_rounds by course
      const res = await fetch(`${API}/leaderboard/my-rounds/${encodeURIComponent(entry.course_name)}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Deleted ${data.deleted_count || 1} round(s) from ${entry.course_name}`);
        if (onDeleteRound) onDeleteRound();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to delete round');
      }
    } catch (err) {
      toast.error('Failed to delete round');
    }
  };

  return (
    <>
      <div className="grid grid-cols-12 gap-2 px-6 py-4 bg-bundle-cream/50 border-b border-bundle-cream-dark">
        {compareMode && <div className="col-span-1 font-body text-xs uppercase tracking-wide text-gray-500 font-semibold"></div>}
        <div className={`${compareMode ? 'col-span-3' : 'col-span-1'} font-body text-xs uppercase tracking-wide text-gray-500 font-semibold`}>{compareMode ? '' : '#'}</div>
        <div className="col-span-3 font-body text-xs uppercase tracking-wide text-gray-500 font-semibold">Course</div>
        <div className="col-span-2 font-body text-xs uppercase tracking-wide text-gray-500 font-semibold text-center">Best Score</div>
        <div className="col-span-1 font-body text-xs uppercase tracking-wide text-gray-500 font-semibold text-center">vs Par</div>
        {!compareMode && (
          <>
            <div className="col-span-2 font-body text-xs uppercase tracking-wide text-gray-500 font-semibold text-center">Avg Score</div>
            <div className="col-span-1 font-body text-xs uppercase tracking-wide text-gray-500 font-semibold text-center">Rounds</div>
            <div className="col-span-2 font-body text-xs uppercase tracking-wide text-gray-500 font-semibold text-right"></div>
          </>
        )}
        {compareMode && (
          <>
            <div className="col-span-1 font-body text-xs uppercase tracking-wide text-gray-500 font-semibold text-center">Rounds</div>
            <div className="col-span-1 font-body text-xs uppercase tracking-wide text-gray-500 font-semibold text-right">Avg</div>
          </>
        )}
      </div>
      {loadingData ? (
        <div className="p-12 text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-bundle-green mx-auto"></div>
        </div>
      ) : leaderboard.length === 0 ? (
        <EmptyState message="Complete a live round to track your progress!" />
      ) : (
        <div className="divide-y divide-bundle-cream-dark">
          {leaderboard.map((entry, index) => {
            const isSelected = selectedCourses.includes(entry.course_name);
            const isFiltered = trendCourseFilter === entry.course_name;
            return (
              <div
                key={index}
                data-testid={`my-round-row-${index}`}
                className={`grid grid-cols-12 gap-2 px-6 py-4 items-center transition-colors cursor-pointer ${
                  isSelected ? 'bg-bundle-green/10 border-l-4 border-bundle-green' :
                  isFiltered ? 'bg-bundle-gold/10 border-l-4 border-bundle-gold' :
                  'hover:bg-bundle-cream/30'
                } ${!compareMode && !isFiltered && entry.rank <= 3 ? 'bg-bundle-cream/20' : ''}`}
                onClick={() => {
                  if (compareMode) {
                    toggleCourseSelection(entry.course_name);
                  } else if (setTrendCourseFilter) {
                    setTrendCourseFilter(prev => prev === entry.course_name ? null : entry.course_name);
                  }
                }}
              >
                {compareMode && (
                  <div className="col-span-1 flex items-center justify-center">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                      isSelected ? 'bg-bundle-green border-bundle-green' : 'border-gray-300'
                    }`}>
                      {isSelected && <Check className="w-3 h-3 text-white" />}
                    </div>
                  </div>
                )}
                <div className={`${compareMode ? 'col-span-3' : 'col-span-1'} flex items-center justify-center`}>
                  {!compareMode && getRankIcon(entry.rank)}
                </div>
                <div className="col-span-3">
                  <p className={`font-body font-semibold truncate flex items-center gap-1 ${isFiltered ? 'text-bundle-gold-dark' : 'text-bundle-green'}`}>
                    {entry.course_name}
                    {isFiltered && <TrendingUp className="w-3 h-3 text-bundle-gold flex-shrink-0" />}
                  </p>
                </div>
                <div className="col-span-2 text-center">
                  <span className="font-heading text-lg font-bold text-bundle-green">{entry.best_score}</span>
                </div>
                <div className="col-span-1 text-center">
                  <span className={`font-body font-semibold text-sm ${
                    entry.best_vs_par < 0 ? 'text-red-600' : entry.best_vs_par === 0 ? 'text-bundle-green' : 'text-gray-700'
                  }`}>
                    {formatVsPar(entry.best_vs_par)}
                  </span>
                </div>
                {!compareMode ? (
                  <>
                    <div className="col-span-2 text-center">
                      <span className="font-body text-sm text-gray-600">{entry.avg_score}</span>
                    </div>
                    <div className="col-span-1 text-center">
                      <span className="inline-flex items-center justify-center w-7 h-7 bg-bundle-green/10 rounded-full">
                        <span className="font-body text-xs font-semibold text-bundle-green">{entry.rounds_played}</span>
                      </span>
                    </div>
                    <div className="col-span-2 text-right">
                      <button
                        onClick={(e) => handleDelete(e, entry)}
                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
                        title="Delete rounds at this course"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="col-span-1 text-center">
                      <span className="font-body text-xs text-gray-500">{entry.rounds_played}</span>
                    </div>
                    <div className="col-span-1 text-right">
                      <span className="font-body text-xs text-gray-500">{entry.avg_score}</span>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

const COMPARE_STATS = [
  { key: 'rounds_played', label: 'Rounds Played', format: v => v, best: 'max' },
  { key: 'best_score', label: 'Best Score', format: v => v, best: 'min' },
  { key: 'worst_score', label: 'Worst Score', format: v => v, best: 'min' },
  { key: 'avg_score', label: 'Avg Score', format: v => v, best: 'min' },
  { key: 'best_vs_par', label: 'Best vs Par', format: (v, fvp) => fvp(v), best: 'min' },
  { key: 'avg_vs_par', label: 'Avg vs Par', format: (v, fvp) => fvp(v), best: 'min' },
  { key: 'total_birdies', label: 'Total Birdies', format: v => v, best: 'max' },
  { key: 'total_eagles', label: 'Total Eagles', format: v => v, best: 'max' },
  { key: 'total_pars', label: 'Total Pars', format: v => v, best: 'max' },
  { key: 'total_bogeys', label: 'Total Bogeys', format: v => v, best: 'min' },
  { key: 'avg_putts', label: 'Avg Putts/Hole', format: v => v, best: 'min' },
];

function ComparisonView({ data, formatVsPar, onClose }) {
  const bestVal = (key, mode) => {
    const vals = data.map(d => d[key]).filter(v => v !== undefined && v !== null);
    return mode === 'min' ? Math.min(...vals) : Math.max(...vals);
  };

  return (
    <div data-testid="comparison-view">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-xl font-bold text-bundle-green flex items-center gap-2">
          <BarChart3 className="w-5 h-5" /> Course Comparison
        </h2>
        <Button data-testid="close-compare-btn" variant="ghost" size="sm" onClick={onClose}>
          <X className="w-4 h-4 mr-1" /> Back to My Rounds
        </Button>
      </div>

      <Card className="bg-white border-bundle-cream-dark overflow-hidden">
        <CardContent className="p-0">
          {/* Header row with course names */}
          <div className="grid gap-0 border-b border-bundle-cream-dark" style={{ gridTemplateColumns: `180px repeat(${data.length}, 1fr)` }}>
            <div className="px-4 py-3 bg-bundle-cream/50 font-body text-xs uppercase tracking-wide text-gray-500 font-semibold">Stat</div>
            {data.map((c, i) => (
              <div key={i} className="px-4 py-3 bg-bundle-cream/50 text-center border-l border-bundle-cream-dark">
                <p className="font-body font-bold text-sm text-bundle-green truncate">{c.course_name}</p>
              </div>
            ))}
          </div>

          {/* Stat rows */}
          {COMPARE_STATS.map((stat, si) => {
            const best = bestVal(stat.key, stat.best);
            return (
              <div
                key={stat.key}
                className={`grid gap-0 border-b border-bundle-cream-dark last:border-0 ${si % 2 === 0 ? 'bg-white' : 'bg-bundle-cream/20'}`}
                style={{ gridTemplateColumns: `180px repeat(${data.length}, 1fr)` }}
              >
                <div className="px-4 py-3 font-body text-sm text-gray-700 font-medium">{stat.label}</div>
                {data.map((c, i) => {
                  const val = c[stat.key];
                  const isBest = val === best && data.filter(d => d[stat.key] === best).length < data.length;
                  return (
                    <div key={i} className="px-4 py-3 text-center border-l border-bundle-cream-dark">
                      <span className={`font-body text-sm font-semibold ${isBest ? 'text-bundle-green' : 'text-gray-700'}`}>
                        {stat.format(val, formatVsPar)}
                        {isBest && <span className="ml-1 text-xs text-bundle-gold">*</span>}
                      </span>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </CardContent>
      </Card>
      <p className="mt-2 font-body text-xs text-gray-400 text-right">* Best in comparison</p>
    </div>
  );
}

function EmptyState({ message, actionLabel, onAction }) {
  return (
    <div className="p-12 text-center" data-testid="leaderboard-empty">
      <Trophy className="w-16 h-16 text-gray-300 mx-auto mb-4" />
      <h3 className="font-heading text-xl text-bundle-green mb-2">No Data Yet</h3>
      <p className="font-body text-gray-500 mb-6">{message}</p>
      {actionLabel && onAction && (
        <Button onClick={onAction} className="btn-bundle">{actionLabel}</Button>
      )}
    </div>
  );
}

function TeamsTable({ loadingData, leaderboard, getRankIcon, user }) {
  return (
    <>
      <div className="grid grid-cols-12 gap-2 px-6 py-4 bg-bundle-cream/50 border-b border-bundle-cream-dark">
        <div className="col-span-1 font-body text-xs uppercase tracking-wide text-gray-500 font-semibold">Rank</div>
        <div className="col-span-5 font-body text-xs uppercase tracking-wide text-gray-500 font-semibold">Pair</div>
        <div className="col-span-1 font-body text-xs uppercase tracking-wide text-gray-500 font-semibold text-center">Wins</div>
        <div className="col-span-2 font-body text-xs uppercase tracking-wide text-gray-500 font-semibold text-center">F9 / B9</div>
        <div className="col-span-1 font-body text-xs uppercase tracking-wide text-gray-500 font-semibold text-center">Best</div>
        <div className="col-span-2 font-body text-xs uppercase tracking-wide text-gray-500 font-semibold text-right">Winnings</div>
      </div>
      {loadingData ? (
        <div className="p-12 text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-bundle-green mx-auto"></div>
        </div>
      ) : leaderboard.length === 0 ? (
        <EmptyState message="Post a team round (2+ players, Teams toggle on) to start the pairs leaderboard!" />
      ) : (
        <div className="divide-y divide-bundle-cream-dark">
          {leaderboard.map((entry, index) => {
            const myName = (user?.name || '').toLowerCase().trim();
            const isMyTeam = entry.members?.some(m => m.toLowerCase().trim() === myName);
            return (
              <div
                key={index}
                data-testid={`team-row-${index}`}
                className={`grid grid-cols-12 gap-2 px-6 py-4 items-center transition-colors hover:bg-bundle-cream/30 ${isMyTeam ? 'rank-highlight bg-bundle-gold/5' : ''} ${entry.rank <= 3 ? 'bg-bundle-cream/20' : ''}`}
              >
                <div className="col-span-1 flex items-center justify-center">{getRankIcon(entry.rank)}</div>
                <div className="col-span-5">
                  <p className={`font-body font-bold ${isMyTeam ? 'text-bundle-gold-dark' : 'text-bundle-green'} truncate`}>
                    {entry.pair_key}
                    {isMyTeam && <span className="ml-2 text-xs text-bundle-gold">(You)</span>}
                  </p>
                  <p className="text-[11px] text-gray-500 truncate">
                    {entry.rounds_played} round{entry.rounds_played === 1 ? '' : 's'}
                    {entry.recent_courses?.length > 0 && <> · {entry.recent_courses[0]}</>}
                  </p>
                </div>
                <div className="col-span-1 text-center">
                  <span className="inline-flex items-center justify-center w-8 h-8 bg-bundle-green/10 rounded-full">
                    <span className="font-body font-semibold text-bundle-green">{entry.wins}</span>
                  </span>
                </div>
                <div className="col-span-2 text-center">
                  <span className="font-body text-sm text-gray-600">{entry.f9_wins} / {entry.b9_wins}</span>
                </div>
                <div className="col-span-1 text-center">
                  <span className="font-heading font-bold text-bundle-green text-sm">{entry.best_score ?? '—'}</span>
                </div>
                <div className="col-span-2 text-right">
                  <span className="font-heading text-lg font-bold text-bundle-green">${entry.total_winnings?.toFixed(0) || 0}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

